import React, {useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex} from '@salesforce/retail-react-app/app/components/shared/ui'
import Swatch from '../../swatch-group/swatch'
import {filterImageGroups, getDecoratedVariationAttributes} from '../../../utils/product-utils'
import {useProductAvailability} from '../../../hooks/use-product-availability'
import {getRollupColorSwatches} from '../utils/product-tile-utils'

const ProductTileDetailsColorSwatches = ({
    product,
    styles,
    colorValue,
    setColorValue,
    setProductId,
    productId,
    isNearViewport = true
}) => {
    const sourceProduct = useMemo(() => {
        if (product?.variationAttributes?.length && product?.imageGroups?.length) {
            return product
        }
        if (product?.representedProduct?.variationAttributes?.length) {
            return product.representedProduct
        }
        return product
    }, [product])

    const decoratedVariationAttributes = useMemo(
        () => getDecoratedVariationAttributes(sourceProduct) || [],
        [sourceProduct]
    )

    const colorAttribute = useMemo(() => {
        const decoratedColor = decoratedVariationAttributes.find(({id}) => id === 'color')
        if (decoratedColor) return decoratedColor
        return sourceProduct?.variationAttributes?.find(({id}) => id === 'color')
    }, [decoratedVariationAttributes, sourceProduct?.variationAttributes])

    const values = colorAttribute?.values || []

    // Precomputed colors off the search hit — the only color source available during SSR.
    const rollupValues = useMemo(() => getRollupColorSwatches(product), [product])

    const derivedValues = useMemo(() => {
        if (values.length) return values

        const variationGroups =
            product?.variationGroups ||
            product?.variants ||
            sourceProduct?.variationGroups ||
            sourceProduct?.variants ||
            []

        const colorValues = variationGroups
            .map((group) => group?.variationValues?.color)
            .filter(Boolean)

        const uniqueColorValues = Array.from(new Set(colorValues))

        if (!uniqueColorValues.length) return []

        return uniqueColorValues.map((value) => {
            const swatchImage = filterImageGroups(sourceProduct?.imageGroups, {
                viewType: 'swatch',
                variationValues: {color: value}
            })?.[0]?.images?.[0]

            return {
                value,
                name: value,
                swatch: swatchImage
            }
        })
    }, [values, product?.variationGroups, product?.variants, sourceProduct])

    // Live variation data wins; the rollup only fills in until it loads.
    const effectiveValues = values.length
        ? values
        : derivedValues.length
        ? derivedValues
        : rollupValues
    const isRollupSource =
        !values.length && !derivedValues.length && rollupValues.length > 0

    const selectedColorFromProductId = useMemo(() => {
        if (!productId) return null
        const variationGroups =
            product?.variationGroups ||
            product?.variants ||
            sourceProduct?.variationGroups ||
            sourceProduct?.variants ||
            []
        const matchedGroup = variationGroups.find((group) => group?.productId === productId)
        if (matchedGroup?.variationValues?.color) return matchedGroup.variationValues.color
        return rollupValues.find((swatch) => swatch.productId === productId)?.value || null
    }, [productId, product, sourceProduct, rollupValues])

    const selectedColor =
        colorValue ||
        selectedColorFromProductId ||
        sourceProduct?.variationValues?.color ||
        product?.variationValues?.color ||
        sourceProduct?.c_color ||
        product?.c_color ||
        null

    const handleColorChange = useCallback(
        (value) => {
            if (!value) return

            const variationGroups =
                product?.variationGroups ||
                product?.variants ||
                sourceProduct?.variationGroups ||
                sourceProduct?.variants ||
                []
            const matchedGroup = variationGroups.find(
                (group) => group?.variationValues?.color === value
            )
            const nextProductId =
                matchedGroup?.productId ||
                rollupValues.find((swatch) => swatch.value === value)?.productId

            if (nextProductId) {
                setProductId?.(nextProductId)
            }

            if (setColorValue) {
                setColorValue(value)
            }
        },
        [
            product?.variationGroups,
            product?.variants,
            sourceProduct,
            rollupValues,
            setProductId,
            setColorValue
        ]
    )

    // Order by the represented (default) color — stable across selection and remounts, so the
    // row doesn't reshuffle on interaction.
    const orderingColor =
        product?.representedProduct?.c_color ||
        sourceProduct?.representedProduct?.c_color ||
        sourceProduct?.variationValues?.color ||
        product?.variationValues?.color ||
        sourceProduct?.c_color ||
        product?.c_color ||
        null

    // Map each color -> its variation group product id so we can look up per-color availability
    // and hide colors that are entirely out of stock (same idea as the PDP color swatches).
    const colorToProductId = useMemo(() => {
        const variationGroups =
            product?.variationGroups ||
            product?.variants ||
            sourceProduct?.variationGroups ||
            sourceProduct?.variants ||
            []
        const map = {}
        variationGroups.forEach((group) => {
            const color = group?.variationValues?.color
            if (color && group?.productId && !(color in map)) {
                map[color] = group.productId
            }
        })
        // Fill in any color the live groups don't cover.
        rollupValues.forEach(({value, productId: rollupProductId}) => {
            if (value && rollupProductId && !(value in map)) {
                map[value] = rollupProductId
            }
        })
        return map
    }, [product?.variationGroups, product?.variants, sourceProduct, rollupValues])

    // The job's `recheck: false` holds even after live variation data replaces the rollup; check
    // the rest and any color it doesn't know. Sorted so both phases share one query key.
    const colorGroupIds = useMemo(() => {
        const recheckByColor = new Map(rollupValues.map(({value, recheck}) => [value, recheck]))
        const ids = effectiveValues
            .filter(({value}) => !rollupValues.length || recheckByColor.get(value) !== false)
            .map(({value}) => colorToProductId[value])
            .filter(Boolean)
        return [...new Set(ids)].sort()
    }, [effectiveValues, rollupValues, colorToProductId])

    // Single-color tiles render no swatch row. The fetch waits for the row to approach the
    // viewport, so stock is known before a hover and hidden (mobile) rows never ask.
    const hasColorsToCheck = effectiveValues.length > 1 && colorGroupIds.length > 0
    const {data: colorAvailabilityMap, isFetched: isColorAvailabilityFetched} =
        useProductAvailability(colorGroupIds, {
            enabled: hasColorsToCheck && isNearViewport,
            staleTime: 5 * 60 * 1000
        })

    // Without the rollup, hold the row back until availability resolves so out-of-stock colors
    // don't paint then vanish. The rollup set is already filtered, so it can paint immediately.
    const isColorAvailabilityPending =
        !isRollupSource && hasColorsToCheck && !isColorAvailabilityFetched

    const isColorOrderable = useCallback(
        (colorValue) => {
            const groupProductId = colorToProductId[colorValue]
            const orderable = groupProductId
                ? colorAvailabilityMap?.[groupProductId]?.inventory?.orderable
                : undefined
            // Fail open: keep the color unless availability explicitly reports it not orderable.
            return orderable !== false
        },
        [colorToProductId, colorAvailabilityMap]
    )

    // Colors that have at least one variant configured for notify-me (restock). Such a color stays
    // visible even when it is entirely out of stock, so shoppers can still request a restock alert.
    // Computed on the backend (search hit `c_notifyMeColors`) since the tile only has swatch-level data.
    const notifyMeColors = product?.c_notifyMeColors || sourceProduct?.c_notifyMeColors

    // Up to 5 swatches; filter offline before slicing, then pin orderingColor first.
    const swatchValues = useMemo(() => {
        const list = effectiveValues.filter(
            (value) =>
                value?.online !== false &&
                (isColorOrderable(value?.value) || notifyMeColors?.includes(value?.value))
        )
        const selectedIndex = list.findIndex((value) => value?.value === orderingColor)
        const ordered =
            selectedIndex > 0
                ? [
                      list[selectedIndex],
                      ...list.slice(0, selectedIndex),
                      ...list.slice(selectedIndex + 1)
                  ]
                : list
        return ordered.slice(0, 5)
    }, [effectiveValues, orderingColor, isColorOrderable, notifyMeColors])

    if (isColorAvailabilityPending) {
        return null
    }

    if (!swatchValues.length) {
        return null
    }

    return (
        <Box
            {...styles.colorSwatchCarouselContainer}
            onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
            onMouseDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
        >
            <Flex {...styles.colorSwatchRow} role="radiogroup" aria-label="Colors">
                {swatchValues.map((value) => {
                    if (value?.online === false) {
                        return null
                    }

                    const swatchImage = value?.image || value?.swatch
                    const swatchImageUrl = swatchImage?.disBaseLink || swatchImage?.link
                    const isSelected = selectedColor === value?.value

                    const content = swatchImageUrl ? (
                        <Box
                            {...styles.colorSwatchImage}
                            backgroundColor={
                                typeof value?.name === 'string'
                                    ? value.name.toLowerCase()
                                    : undefined
                            }
                            backgroundImage={`url(${swatchImageUrl})`}
                        />
                    ) : (
                        <Box {...styles.colorSwatchText}>{value?.name}</Box>
                    )

                    return (
                        <Swatch
                            key={value?.value}
                            value={value?.value}
                            name={value?.name}
                            isProductTile
                            variant="circle"
                            selected={isSelected}
                            disabled={value?.orderable === false}
                            isFocusable={true}
                            handleSelect={handleColorChange}
                            {...styles.colorSwatchItem}
                        >
                            {content}
                        </Swatch>
                    )
                })}
            </Flex>
        </Box>
    )
}

ProductTileDetailsColorSwatches.propTypes = {
    product: PropTypes.object,
    styles: PropTypes.object,
    colorValue: PropTypes.string,
    setColorValue: PropTypes.func,
    setProductId: PropTypes.func,
    productId: PropTypes.string,
    isNearViewport: PropTypes.bool
}

export default ProductTileDetailsColorSwatches
